import type {
  ConnectWorkflow,
  ConnectWorkflowEdge,
  ConnectWorkflowNode,
} from '../../../services/connect/connect-workflows.service';
import {
  ConnectWorkflowNodePaletteItem,
  isConnectGuidedBindingToken,
  isConnectGuidedCurrentItemBindingToken,
  isConnectGuidedRuntimeLikeString,
  parseConnectGuidedPriorStepBindingToken,
} from '../../../services/connect/connect-nodes.service';
import {
  compileGuidedWorkflowWithTrace,
  ConnectGuidedBlock,
  ConnectGuidedCompilationTrace,
  ConnectGuidedConnectorProjector,
  ConnectGuidedNodeRole,
  ConnectGuidedPriorityDecisionBlock,
  ConnectGuidedWorkflowOutline,
  projectGuidedWorkflowOutline,
} from './connect-guided-workflow-compiler';

const GUIDED_TAGS = new Set(['guided', 'provider-free', 'local-demo', 'connected-account']);
const LOCAL_PREVIEW_CONNECTION = 'local_preview';
const RUNTIME_NODE_FIELDS = new Set([
  'input',
  'connection',
  'category',
  'objectType',
  'condition',
  'wait',
  'loop',
  'x-ai',
  'x-cache',
  'timeout_ms',
  'retries',
  'on_error',
  'idempotency_key',
]);

export class ConnectGuidedWorkflowJsonAdapterSession {
  readonly outline: ConnectGuidedWorkflowOutline;

  private readonly source: ConnectWorkflow;
  private readonly catalog: readonly ConnectWorkflowNodePaletteItem[] | undefined;
  private readonly originalOutline: ConnectGuidedWorkflowOutline;
  private readonly originalIdentityShape: unknown;
  private readonly triggerNodeId: string;
  private readonly blockNodeIds = new Map<ConnectGuidedBlock, string>();
  private readonly blockKinds = new Map<ConnectGuidedBlock, ConnectGuidedBlock['kind']>();
  private readonly blockSignatures = new Map<ConnectGuidedBlock, string>();
  private readonly decisionOutcomeIds = new Map<
    ConnectGuidedPriorityDecisionBlock,
    {urgent: string; standard: string}
  >();

  private constructor(
    source: ConnectWorkflow,
    outline: ConnectGuidedWorkflowOutline,
    canonicalToSourceId: Map<string, string>,
    catalog?: readonly ConnectWorkflowNodePaletteItem[],
  ) {
    this.source = cloneJson(source);
    this.catalog = catalog ? cloneJson(catalog) : undefined;
    this.outline = outline;
    this.originalOutline = cloneJson(outline);

    const sourceToCanonicalId = invertNodeIdMap(canonicalToSourceId);
    remapOutlinePriorBindings(outline, sourceToCanonicalId);
    let trace: ConnectGuidedCompilationTrace;
    try {
      trace = compileGuidedWorkflowWithTrace(outline, undefined, this.catalog);
    } finally {
      remapOutlinePriorBindings(outline, canonicalToSourceId);
    }
    this.triggerNodeId = requiredMappedId(canonicalToSourceId, trace.triggerNodeId);
    trace.blockNodeIds.forEach((nodeId, block) => {
      this.blockNodeIds.set(block, requiredMappedId(canonicalToSourceId, nodeId));
      this.blockKinds.set(block, block.kind);
      this.blockSignatures.set(block, blockSignature(block));
    });
    trace.decisionOutcomeIds.forEach((nodeIds, block) => {
      this.decisionOutcomeIds.set(block, {
        urgent: requiredMappedId(canonicalToSourceId, nodeIds.urgent),
        standard: requiredMappedId(canonicalToSourceId, nodeIds.standard),
      });
    });
    assertUnambiguousSource(this.source);
    this.originalIdentityShape = this.identityShape(this.outline);
  }

  get canRunFromGuidedEditor(): boolean {
    const declared = readSourceConnectionDeclarations(this.source['connections']);
    if (!declared) return false;
    const owned = collectGuidedConnections(this.outline);
    return declared.size === owned.size
      && [...owned].every(([name, ref]) => declared.get(name)?.ref === ref);
  }

  getTriggerNodeId(): string {
    return this.triggerNodeId;
  }

  isOutcomeNodeId(nodeId: string, route: 'urgent' | 'standard'): boolean {
    return [...this.decisionOutcomeIds.values()].some(ids => ids[route] === nodeId);
  }

  static open(
    workflow: ConnectWorkflow,
    projectConnector?: ConnectGuidedConnectorProjector,
    catalog?: readonly ConnectWorkflowNodePaletteItem[],
  ): ConnectGuidedWorkflowJsonAdapterSession | null {
    if (!isRecord(workflow) || Object.prototype.hasOwnProperty.call(workflow, 'ai_export')) return null;
    try {
      const source = cloneJson(workflow);
      const projection = createProjectionCandidate(source, projectConnector);
      const outline = projectGuidedWorkflowOutline(projection.workflow, projectConnector, catalog);
      if (!outline) return null;
      remapOutlinePriorBindings(outline, projection.canonicalToSourceId);
      return new ConnectGuidedWorkflowJsonAdapterSession(
        source,
        outline,
        projection.canonicalToSourceId,
        catalog,
      );
    } catch {
      return null;
    }
  }

  hasChanges(name: string): boolean {
    const outline = this.currentOutline(name);
    return !jsonEqual(outline, this.originalOutline)
      || !jsonEqual(this.identityShape(outline), this.originalIdentityShape);
  }

  serialize(name: string): ConnectWorkflow {
    const outline = this.currentOutline(name);
    this.assertMappedRoleIntegrity(outline);
    if (jsonEqual(outline, this.originalOutline)
      && jsonEqual(this.identityShape(outline), this.originalIdentityShape)) {
      return cloneJson(this.source);
    }

    const retainedIds = this.collectRetainedIds(outline);
    const retainedNodeIds = new Set(retainedIds.values());
    retainedNodeIds.add(this.triggerNodeId);
    const usedIds = new Set((this.source.nodes ?? []).map(node => node.id));
    const assignedIds = new Map<string, string>();
    const resolveNodeId = (
      block: ConnectGuidedBlock | null,
      role: ConnectGuidedNodeRole,
      suggestedId: string,
    ): string => {
      const retained = this.retainedId(block, role);
      if (retained) return retained;
      const allocationKey = `${role}:${suggestedId}`;
      const assigned = assignedIds.get(allocationKey);
      if (assigned) return assigned;
      const allocated = allocateUniqueId(suggestedId, usedIds);
      assignedIds.set(allocationKey, allocated);
      return allocated;
    };

    const next = compileGuidedWorkflowWithTrace(outline, resolveNodeId, this.catalog);
    const result = cloneJson(this.source);
    result.name = next.workflow.name;
    result.nodes = this.mergeNodes(next, retainedNodeIds);
    result.edges = this.mergeEdges(next.workflow.edges ?? []);
    patchGuidedTags(result, this.source.tags, next.workflow.tags);
    patchGuidedConnections(
      result,
      this.source['connections'],
      collectGuidedConnections(this.originalOutline),
      collectGuidedConnections(outline),
    );
    return result;
  }

  previewBlockNodeIds(): Map<ConnectGuidedBlock, string> {
    const outline = this.currentOutline(this.outline.name);
    const usedIds = new Set((this.source.nodes ?? []).map(node => node.id));
    const assignedIds = new Map<string, string>();
    const resolveNodeId = (
      block: ConnectGuidedBlock | null,
      role: ConnectGuidedNodeRole,
      suggestedId: string,
    ): string => {
      const retained = this.retainedId(block, role);
      if (retained) return retained;
      const allocationKey = `${role}:${suggestedId}`;
      const assigned = assignedIds.get(allocationKey);
      if (assigned) return assigned;
      const allocated = allocateUniqueId(suggestedId, usedIds);
      assignedIds.set(allocationKey, allocated);
      return allocated;
    };
    return compileGuidedWorkflowWithTrace(outline, resolveNodeId, this.catalog).blockNodeIds;
  }

  private currentOutline(name: string): ConnectGuidedWorkflowOutline {
    return {name, blocks: this.outline.blocks};
  }

  private assertMappedRoleIntegrity(outline: ConnectGuidedWorkflowOutline): void {
    visitBlocks(outline.blocks, block => {
      const expected = this.blockKinds.get(block);
      if (expected && expected !== block.kind) {
        throw new Error('Workflow block identity changed role.');
      }
      const signature = this.blockSignatures.get(block);
      if (signature && signature !== blockSignature(block)) {
        throw new Error('Workflow block authority changed without replacement.');
      }
    });
  }

  private collectRetainedIds(outline: ConnectGuidedWorkflowOutline): Map<string, string> {
    const retained = new Map<string, string>();
    visitBlocks(outline.blocks, block => {
      const nodeId = this.blockNodeIds.get(block);
      if (nodeId) retained.set(`block:${nodeId}`, nodeId);
      if (block.kind === 'priority-decision') {
        const outcomes = this.decisionOutcomeIds.get(block);
        if (outcomes) {
          retained.set(`urgent:${outcomes.urgent}`, outcomes.urgent);
          retained.set(`standard:${outcomes.standard}`, outcomes.standard);
        }
      }
    });
    return retained;
  }

  private retainedId(
    block: ConnectGuidedBlock | null,
    role: ConnectGuidedNodeRole,
  ): string | null {
    if (role === 'start') return this.triggerNodeId;
    if (!block) return null;
    if (role === 'urgent-outcome' || role === 'standard-outcome') {
      if (block.kind !== 'priority-decision') return null;
      const outcomes = this.decisionOutcomeIds.get(block);
      return role === 'urgent-outcome' ? outcomes?.urgent ?? null : outcomes?.standard ?? null;
    }
    return this.blockNodeIds.get(block) ?? null;
  }

  private identityShape(outline: ConnectGuidedWorkflowOutline): unknown {
    const shape = (blocks: ConnectGuidedBlock[]): unknown[] => blocks.map(block => ({
      kind: block.kind,
      nodeId: this.blockNodeIds.get(block) ?? null,
      ...(block.kind === 'for-each-item' ? {body: shape(block.body)} : {}),
      ...(block.kind === 'connector-operation' ? {
        operationRef: block.operationRef,
        connectionSlot: block.connectionSlot,
        connectionProviderRef: block.connectionProviderRef,
      } : {}),
      ...(block.kind === 'priority-decision' ? {
        outcomes: this.decisionOutcomeIds.get(block) ?? null,
        urgent: shape(block.urgent.blocks),
        standard: shape(block.standard.blocks),
      } : {}),
    }));
    return shape(outline.blocks);
  }

  private mergeNodes(
    next: ConnectGuidedCompilationTrace,
    retainedNodeIds: Set<string>,
  ): ConnectWorkflowNode[] {
    const originals = indexNodes(this.source.nodes);
    const compiledById = indexNodes(next.workflow.nodes);
    const decisionIds = new Set<string>();
    const outcomeIds = new Map<string, 'urgent' | 'standard'>();
    const connectorIds = new Set<string>();
    next.blockNodeIds.forEach((nodeId, block) => {
      if (block.kind === 'connector-operation') connectorIds.add(nodeId);
      if (block.kind !== 'priority-decision') return;
      decisionIds.add(nodeId);
      const outcomes = next.decisionOutcomeIds.get(block);
      if (outcomes) {
        outcomeIds.set(outcomes.urgent, 'urgent');
        outcomeIds.set(outcomes.standard, 'standard');
      }
    });

    const emitted = new Set<string>();
    const result: ConnectWorkflowNode[] = [];
    const mergeNode = (compiled: ConnectWorkflowNode): ConnectWorkflowNode => {
      const original = retainedNodeIds.has(compiled.id) ? originals.get(compiled.id) : null;
      if (!original) return cloneJson(compiled);
      const retained = cloneJson(original);
      if (decisionIds.has(compiled.id)) {
        const condition = isRecord(retained['condition'])
          ? cloneJson(retained['condition'])
          : {};
        condition['expression'] = readNestedString(compiled, 'condition', 'expression');
        retained['condition'] = condition;
      } else if (outcomeIds.has(compiled.id)) {
        const input = isRecord(retained['input']) ? cloneJson(retained['input']) : {};
        input['outcome'] = readNestedString(compiled, 'input', 'outcome');
        retained['input'] = input;
      } else if (connectorIds.has(compiled.id)) {
        retained['input'] = cloneJson(compiled['input']);
      }
      return retained;
    };
    (this.source.nodes ?? []).forEach(original => {
      const compiled = compiledById.get(original.id);
      if (!compiled || !retainedNodeIds.has(original.id)) return;
      result.push(mergeNode(compiled));
      emitted.add(original.id);
    });
    (next.workflow.nodes ?? []).forEach(compiled => {
      if (emitted.has(compiled.id)) return;
      result.push(mergeNode(compiled));
      emitted.add(compiled.id);
    });
    return result;
  }

  private mergeEdges(compiledEdges: ConnectWorkflowEdge[]): ConnectWorkflowEdge[] {
    const compiled = new Map(compiledEdges.map(edge => [edgeIdentity(edge), edge]));
    const emitted = new Set<string>();
    const usedIds = new Set(
      (this.source.edges ?? [])
        .map(edge => edge.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    );
    const result: ConnectWorkflowEdge[] = [];
    (this.source.edges ?? []).forEach(edge => {
      const key = edgeIdentity(edge);
      if (!compiled.has(key)) return;
      result.push(cloneJson(edge));
      emitted.add(key);
    });
    compiledEdges.forEach(edge => {
      const key = edgeIdentity(edge);
      if (emitted.has(key)) return;
      const created = cloneJson(edge);
      created.id = allocateUniqueId(created.id ?? `${created.source}-${created.target}`, usedIds);
      result.push(created);
      emitted.add(key);
    });
    const finalIds = result
      .map(edge => edge.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
    if (new Set(finalIds).size !== finalIds.length) {
      throw new Error('Workflow edge identity is ambiguous.');
    }
    return result;
  }
}

function createProjectionCandidate(
  source: ConnectWorkflow,
  projectConnector?: ConnectGuidedConnectorProjector,
): {
  workflow: ConnectWorkflow;
  canonicalToSourceId: Map<string, string>;
} {
  if (!Array.isArray(source.nodes) || !Array.isArray(source.edges)) {
    throw new Error('Workflow graph is missing.');
  }
  const sourceConnections = readSourceConnectionDeclarations(source['connections']);
  if (!sourceConnections) throw new Error('Workflow connection authority is ambiguous.');
  const projectedNodes = source.nodes.map(node => projectNode(
    node,
    sourceConnections,
    projectConnector,
  ));
  const graph = canonicalizeGuidedGraph(projectedNodes, source.edges, projectConnector, sourceConnections);
  const sourceToCanonicalId = graph.sourceToCanonicalId;
  const canonicalToSourceId = new Map<string, string>();
  sourceToCanonicalId.forEach((canonical, original) => {
    if (canonicalToSourceId.has(canonical)) throw new Error('Workflow node identity is ambiguous.');
    canonicalToSourceId.set(canonical, original);
  });
  const candidate: ConnectWorkflow = {
    ...(typeof source.id === 'string' ? {id: source.id} : {}),
    name: source.name,
    ...(typeof source['enabled'] === 'boolean' ? {enabled: source['enabled']} : {}),
    ...(typeof source.version === 'number' ? {version: source.version} : {}),
    tags: canonicalGuidedTags(source.tags, projectedNodes),
    nodes: graph.nodeOrder.map(node => remapNodePriorBindings({
      ...node,
      id: requiredMappedId(sourceToCanonicalId, node.id),
    }, sourceToCanonicalId)),
    edges: graph.edgeOrder.map(edge => projectEdge(edge, sourceToCanonicalId)),
  };
  if (typeof source['owner_client_id'] === 'string') {
    candidate['owner_client_id'] = source['owner_client_id'];
  }
  if (typeof source['owner_tenant_id'] === 'string') {
    candidate['owner_tenant_id'] = source['owner_tenant_id'];
  }
  const usedConnections = new Map<string, string>();
  projectedNodes.filter(node => node.type === 'api.connector').forEach(node => {
    const slot = typeof node['connection'] === 'string' ? node['connection'] : '';
    const declaration = sourceConnections.get(slot);
    if (!declaration || declaration.hasConfig) {
      throw new Error('Workflow connection authority is ambiguous.');
    }
    usedConnections.set(slot, declaration.ref);
  });
  if (usedConnections.size > 0) {
    candidate['connections'] = [...usedConnections].map(([name, ref]) => ({name, ref}));
  }
  return {workflow: candidate, canonicalToSourceId};
}

function remapNodePriorBindings(
  node: ConnectWorkflowNode,
  nodeIds: Map<string, string>,
): ConnectWorkflowNode {
  if (node.type !== 'api.connector' || !isRecord(node['input'])) return node;
  return {...node, input: remapPriorBindingValue(node['input'], nodeIds)};
}

function remapOutlinePriorBindings(
  outline: ConnectGuidedWorkflowOutline,
  nodeIds: Map<string, string>,
): void {
  visitBlocks(outline.blocks, block => {
    if (block.kind === 'connector-operation') {
      block.input = remapPriorBindingValue(block.input, nodeIds) as Record<string, unknown>;
    }
  });
}

function invertNodeIdMap(nodeIds: Map<string, string>): Map<string, string> {
  const inverted = new Map<string, string>();
  nodeIds.forEach((target, source) => {
    if (inverted.has(target)) throw new Error('Workflow node identity is ambiguous.');
    inverted.set(target, source);
  });
  return inverted;
}

function remapPriorBindingValue(value: unknown, nodeIds: Map<string, string>): unknown {
  const binding = parseConnectGuidedPriorStepBindingToken(value);
  if (binding) {
    const nodeId = requiredMappedId(nodeIds, binding.producerNodeId);
    return `$.${nodeId}.output.${binding.outputPath}`;
  }
  if (Array.isArray(value)) return value.map(child => remapPriorBindingValue(child, nodeIds));
  if (isRecord(value)) return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [key, remapPriorBindingValue(child, nodeIds)]),
  );
  return value;
}

function projectNode(
  node: ConnectWorkflowNode,
  connections: Map<string, {ref: string; hasConfig: boolean}>,
  projectConnector?: ConnectGuidedConnectorProjector,
): ConnectWorkflowNode {
  assertGuidedRuntimeShape(node);
  const projected: ConnectWorkflowNode = {id: node.id, type: node.type};
  if (typeof node.ref === 'string') projected.ref = node.ref;
  if (node.type === 'api.connector') {
    const slot = typeof node['connection'] === 'string' ? node['connection'] : '';
    const declaration = connections.get(slot);
    if (!declaration || declaration.hasConfig) {
      throw new Error('Workflow connection authority is ambiguous.');
    }
    projected['connection'] = slot;
    if (node.ref === 'local.preview.verify'
      && (slot !== LOCAL_PREVIEW_CONNECTION || declaration.ref !== 'local.preview')) {
      throw new Error('Workflow connector input is invalid.');
    }
    if (node.ref === 'local.preview.verify' && jsonEqual(node['input'], {probe: 'ready'})) {
      projected['input'] = {probe: 'ready'};
    } else {
      const block = projectConnector?.(node, declaration.ref);
      if (!block || block.connectionSlot !== slot
        || block.connectionProviderRef !== declaration.ref
        || block.operationRef !== node.ref) {
        throw new Error('Workflow connector input is invalid.');
      }
      projected['input'] = cloneJson(block.input);
    }
  } else if (node.type === 'control.loop') {
    const loop = isRecord(node['loop']) ? node['loop'] : {};
    projected['loop'] = {
      collection: cloneJson(loop['collection']),
      item_alias: cloneJson(loop['item_alias']),
      max_items: cloneJson(loop['max_items']),
    };
  } else if (node.type === 'control.condition') {
    const condition = isRecord(node['condition']) ? node['condition'] : {};
    projected['condition'] = {expression: cloneJson(condition['expression'])};
  } else if (node.type === 'control.transform') {
    const input = isRecord(node['input']) ? cloneJson(node['input']) : node['input'];
    if (isRecord(input)) {
      const route = input['route'];
      if (route === 'urgent' || route === 'standard') {
        projected['input'] = {
          outcome: input['outcome'],
          subject: input['subject'],
          details: input['details'],
          route,
        };
      } else {
        projected['input'] = {
          subject: input['subject'],
          details: input['details'],
          priority: input['priority'],
        };
      }
    } else {
      projected['input'] = input;
    }
  }
  return projected;
}

function assertGuidedRuntimeShape(node: ConnectWorkflowNode): void {
  const allowedRuntimeFields = new Set<string>();
  if (node.type === 'control.transform' || node.type === 'api.connector') {
    allowedRuntimeFields.add('input');
  }
  if (node.type === 'api.connector') allowedRuntimeFields.add('connection');
  if (node.type === 'control.loop') allowedRuntimeFields.add('loop');
  if (node.type === 'control.condition') allowedRuntimeFields.add('condition');

  Object.keys(node).forEach(key => {
    if (RUNTIME_NODE_FIELDS.has(key) && !allowedRuntimeFields.has(key)) {
      throw new Error('Workflow contains hidden executable configuration.');
    }
  });

  if (node.type === 'control.transform') {
    const input = node['input'];
    if (!isRecord(input)) throw new Error('Workflow transform input is invalid.');
    const allowed = input['route'] === 'urgent' || input['route'] === 'standard'
      ? ['outcome', 'subject', 'details', 'route']
      : ['subject', 'details', 'priority'];
    if (!hasExactKeys(input, allowed)) {
      throw new Error('Workflow transform input contains hidden executable configuration.');
    }
  }

  if (node.type === 'api.connector') {
    const input = node['input'];
    if (!isRecord(input)) throw new Error('Workflow connector input is invalid.');
  }
}

function hasExactKeys(value: Record<string, unknown>, allowed: string[]): boolean {
  const keys = Object.keys(value);
  return keys.length === allowed.length && keys.every(key => allowed.includes(key));
}

function projectEdge(
  edge: ConnectWorkflowEdge,
  sourceToCanonicalId: Map<string, string>,
): ConnectWorkflowEdge {
  if (Object.prototype.hasOwnProperty.call(edge, 'condition')) {
    throw new Error('Workflow edge contains hidden executable configuration.');
  }
  const source = requiredMappedId(sourceToCanonicalId, edge.source);
  const target = requiredMappedId(sourceToCanonicalId, edge.target);
  return {
    id: `${source}-${target}`,
    source,
    target,
    ...(typeof edge.source_handle === 'string' ? {source_handle: edge.source_handle} : {}),
    ...(typeof edge.label === 'string' ? {label: edge.label} : {}),
  };
}

function patchGuidedTags(
  target: ConnectWorkflow,
  sourceTags: unknown,
  nextTags: string[] | undefined,
): void {
  const source = Array.isArray(sourceTags)
    ? sourceTags.filter((tag): tag is string => typeof tag === 'string')
    : [];
  const next = nextTags ?? [];
  const currentManaged = source.filter(tag => GUIDED_TAGS.has(tag));
  if (currentManaged.length === next.length && next.every(tag => currentManaged.includes(tag))) return;

  const firstManagedIndex = source.findIndex(tag => GUIDED_TAGS.has(tag));
  const custom = source.filter(tag => !GUIDED_TAGS.has(tag));
  const insertAt = firstManagedIndex < 0 ? 0 : Math.min(firstManagedIndex, custom.length);
  target.tags = [...custom.slice(0, insertAt), ...next, ...custom.slice(insertAt)];
}

function patchGuidedConnections(
  target: ConnectWorkflow,
  sourceConnections: unknown,
  previousOwned: Map<string, string>,
  nextOwned: Map<string, string>,
): void {
  const source = Array.isArray(sourceConnections) ? cloneJson(sourceConnections) : [];
  const emitted = new Set<string>();
  const preserved = source.filter(connection => {
    if (!isRecord(connection) || typeof connection['name'] !== 'string') return true;
    const previousRef = previousOwned.get(connection['name']);
    if (!previousRef) return true;
    const nextRef = nextOwned.get(connection['name']);
    if (nextRef === previousRef && connection['ref'] === nextRef) {
      emitted.add(connection['name']);
      return true;
    }
    return false;
  });
  nextOwned.forEach((ref, name) => {
    if (!emitted.has(name)) preserved.push({name, ref});
  });
  if (preserved.length === 0) delete target['connections'];
  else target['connections'] = preserved;
}

function collectGuidedConnections(outline: ConnectGuidedWorkflowOutline): Map<string, string> {
  const result = new Map<string, string>();
  visitBlocks(outline.blocks, block => {
    const connection = block.kind === 'verify-local-account'
      ? {name: LOCAL_PREVIEW_CONNECTION, ref: 'local.preview'}
      : block.kind === 'connector-operation'
        ? {name: block.connectionSlot, ref: block.connectionProviderRef}
        : null;
    if (!connection) return;
    const current = result.get(connection.name);
    if (current && current !== connection.ref) {
      throw new Error('Workflow connection authority is ambiguous.');
    }
    result.set(connection.name, connection.ref);
  });
  return result;
}

function readSourceConnectionDeclarations(
  value: unknown,
): Map<string, {ref: string; hasConfig: boolean}> | null {
  const result = new Map<string, {ref: string; hasConfig: boolean}>();
  if (value === undefined) return result;
  if (!Array.isArray(value)) return null;
  for (const candidate of value) {
    if (!isRecord(candidate)
      || typeof candidate['name'] !== 'string'
      || candidate['name'].trim() !== candidate['name']
      || candidate['name'].length === 0
      || typeof candidate['ref'] !== 'string'
      || candidate['ref'].trim() !== candidate['ref']
      || candidate['ref'].length === 0
      || result.has(candidate['name'])) return null;
    result.set(candidate['name'], {
      ref: candidate['ref'],
      hasConfig: Object.prototype.hasOwnProperty.call(candidate, 'config'),
    });
  }
  return result;
}

function indexNodes(nodes: ConnectWorkflowNode[] | undefined): Map<string, ConnectWorkflowNode> {
  const result = new Map<string, ConnectWorkflowNode>();
  (nodes ?? []).forEach(node => result.set(node.id, node));
  return result;
}

function indexEdges(edges: ConnectWorkflowEdge[] | undefined): Map<string, ConnectWorkflowEdge> {
  const result = new Map<string, ConnectWorkflowEdge>();
  (edges ?? []).forEach(edge => {
    const key = edgeIdentity(edge);
    if (result.has(key)) throw new Error('Workflow edge identity is ambiguous.');
    result.set(key, edge);
  });
  return result;
}

function assertUnambiguousSource(source: ConnectWorkflow): void {
  const nodeIds = new Set<string>();
  (source.nodes ?? []).forEach(node => {
    if (nodeIds.has(node.id)) throw new Error('Workflow node identity is ambiguous.');
    nodeIds.add(node.id);
  });
  const edgeIds = new Set<string>();
  (source.edges ?? []).forEach(edge => {
    if (typeof edge.id !== 'string') return;
    if (edgeIds.has(edge.id)) throw new Error('Workflow edge identity is ambiguous.');
    edgeIds.add(edge.id);
  });
  indexEdges(source.edges);
}

function canonicalizeGuidedGraph(
  nodes: ConnectWorkflowNode[],
  edges: ConnectWorkflowEdge[],
  projectConnector: ConnectGuidedConnectorProjector | undefined,
  connectionDeclarations: Map<string, {ref: string; hasConfig: boolean}>,
): {
  sourceToCanonicalId: Map<string, string>;
  nodeOrder: ConnectWorkflowNode[];
  edgeOrder: ConnectWorkflowEdge[];
} {
  const byId = new Map<string, ConnectWorkflowNode>();
  nodes.forEach(node => {
    if (byId.has(node.id)) throw new Error('Workflow node identity is ambiguous.');
    byId.set(node.id, node);
  });
  const start = exactlyOne(nodes.filter(node =>
    node.type === 'trigger.manual' && node.ref === 'system.trigger.manual'));
  const decisions = nodes.filter(node =>
    node.type === 'control.condition' && node.ref === 'system.condition');
  if (decisions.length > 1) throw new Error('Workflow decision authority is ambiguous.');
  const decision = decisions[0] ?? null;
  const urgentOutcomes = nodes.filter(node => isOutcomeNode(node, 'urgent'));
  const standardOutcomes = nodes.filter(node => isOutcomeNode(node, 'standard'));
  if (urgentOutcomes.length > 1 || standardOutcomes.length > 1) {
    throw new Error('Workflow outcome authority is ambiguous.');
  }
  const urgentOutcome = urgentOutcomes[0] ?? null;
  const standardOutcome = standardOutcomes[0] ?? null;
  const outgoing = new Map<string, ConnectWorkflowEdge[]>();
  edges.forEach(edge => {
    if (!byId.has(edge.source) || !byId.has(edge.target)) {
      throw new Error('Workflow graph contains an unresolved node identity.');
    }
    const list = outgoing.get(edge.source) ?? [];
    list.push(edge);
    outgoing.set(edge.source, list);
  });
  const result = new Map<string, string>();
  const nodeOrder: ConnectWorkflowNode[] = [];
  const edgeOrder: ConnectWorkflowEdge[] = [];
  const visitedEdges = new Set<ConnectWorkflowEdge>();
  const visitNode = (node: ConnectWorkflowNode, canonicalId: string): void => {
    if (result.has(node.id)) throw new Error('Workflow graph reuses a guided node.');
    mapNodeId(result, node, canonicalId);
    nodeOrder.push(node);
  };
  const visitEdge = (edge: ConnectWorkflowEdge): void => {
    if (visitedEdges.has(edge)) throw new Error('Workflow graph reuses an edge.');
    visitedEdges.add(edge);
    edgeOrder.push(edge);
  };
  const soleOutgoing = (node: ConnectWorkflowNode): ConnectWorkflowEdge =>
    exactlyOne(outgoing.get(node.id) ?? []);

  const traverseActions = (
    firstNodeId: string,
    terminalNodeId: string | null,
    prefix: string,
  ): void => {
    const occurrences = new Map<string, number>();
    let currentId: string | null = firstNodeId;
    while (currentId !== terminalNodeId) {
      if (currentId === null) throw new Error('Workflow graph ended before its expected terminal.');
      const node = byId.get(currentId);
      if (!node) throw new Error('Workflow graph contains an unresolved node identity.');
      const kind = readActionKind(
        node,
        prefix.includes('for-each-item'),
        projectConnector,
        connectionDeclarations,
      );
      if (!kind) throw new Error('Workflow graph contains an unsupported node.');
      const occurrence = (occurrences.get(kind) ?? 0) + 1;
      occurrences.set(kind, occurrence);
      const suffix = occurrence > 1 ? `-${occurrence}` : '';
      const canonicalId = `${prefix}${kind}${suffix}`;
      visitNode(node, canonicalId);
      if (kind !== 'for-each-item') {
        const nextEdges = outgoing.get(node.id) ?? [];
        if (nextEdges.length === 0 && terminalNodeId === null) {
          currentId = null;
          continue;
        }
        const nextEdge = exactlyOne(nextEdges);
        visitEdge(nextEdge);
        currentId = nextEdge.target;
        continue;
      }

      const nextEdge = soleOutgoing(node);
      visitEdge(nextEdge);
      const body = byId.get(nextEdge.target);
      const bodyKind = body
        ? readActionKind(body, true, projectConnector, connectionDeclarations)
        : null;
      if (!body || !bodyKind || bodyKind === 'for-each-item') {
        throw new Error('Workflow loop body is unsupported.');
      }
      visitNode(body, `${canonicalId}-${bodyKind}`);
      const bodyEdges = outgoing.get(body.id) ?? [];
      if (bodyEdges.length === 0 && terminalNodeId === null) {
        currentId = null;
      } else {
        const bodyNext = exactlyOne(bodyEdges);
        visitEdge(bodyNext);
        currentId = bodyNext.target;
      }
    }
  };

  visitNode(start, 'start');
  const startEdge = soleOutgoing(start);
  visitEdge(startEdge);
  if (!decision) {
    if (urgentOutcome || standardOutcome) throw new Error('Workflow outcome has no decision.');
    traverseActions(startEdge.target, null, '');
    if (result.size !== nodes.length || visitedEdges.size !== edges.length) {
      throw new Error('Workflow graph contains unsupported or disconnected structure.');
    }
    return {sourceToCanonicalId: result, nodeOrder, edgeOrder};
  }
  if (!urgentOutcome || !standardOutcome) throw new Error('Workflow decision outcomes are missing.');
  traverseActions(startEdge.target, decision.id, '');
  visitNode(decision, 'priority-decision');
  const decisionEdges = outgoing.get(decision.id) ?? [];
  const urgentEdge = exactlyOne(decisionEdges.filter(edge => edge.source_handle === 'true'));
  const standardEdge = exactlyOne(decisionEdges.filter(edge => edge.source_handle === 'false'));
  if (decisionEdges.length !== 2) throw new Error('Workflow decision authority is ambiguous.');
  visitEdge(urgentEdge);
  traverseActions(urgentEdge.target, urgentOutcome.id, 'urgent-');
  visitNode(urgentOutcome, 'urgent-outcome');
  if ((outgoing.get(urgentOutcome.id) ?? []).length !== 0) {
    throw new Error('Workflow outcome must be terminal.');
  }
  visitEdge(standardEdge);
  traverseActions(standardEdge.target, standardOutcome.id, 'standard-');
  visitNode(standardOutcome, 'standard-outcome');
  if ((outgoing.get(standardOutcome.id) ?? []).length !== 0) {
    throw new Error('Workflow outcome must be terminal.');
  }
  if (result.size !== nodes.length || visitedEdges.size !== edges.length) {
    throw new Error('Workflow graph contains unsupported or disconnected structure.');
  }
  return {sourceToCanonicalId: result, nodeOrder, edgeOrder};
}

function readActionKind(
  node: ConnectWorkflowNode,
  nestedInLoop: boolean,
  projectConnector: ConnectGuidedConnectorProjector | undefined,
  connectionDeclarations: Map<string, {ref: string; hasConfig: boolean}>,
): string | null {
  if (node.type === 'control.transform' && node.ref === 'system.transform') {
    const expected = nestedInLoop ? {
      subject: '{{item.subject}}',
      details: '{{item.details}}',
      priority: '{{item.priority}}',
    } : {
      subject: '$.manual.subject',
      details: '$.manual.details',
      priority: '$.manual.priority',
    };
    if (jsonEqual(node['input'], expected)) return 'prepare-information';
  }
  if (node.type === 'api.connector' && typeof node.ref === 'string') {
    const slot = typeof node['connection'] === 'string' ? node['connection'] : '';
    const declaration = connectionDeclarations.get(slot);
    const block = declaration && !declaration.hasConfig
      ? projectConnector?.(node, declaration.ref)
      : null;
    if (block?.operationRef === node.ref && block.connectionSlot === slot) {
      if (!hasSupportedGuidedBindings(block.input, nestedInLoop)) return null;
      return moduleNodeId(node.ref);
    }
    if (node.ref === 'local.preview.verify'
      && slot === 'local_preview' && jsonEqual(node['input'], {probe: 'ready'})) {
      return 'verify-local-account';
    }
  }
  if (node.type === 'control.loop' && node.ref === 'system.loop'
    && jsonEqual(node['loop'], {
      collection: '$.ctx.manual.items', item_alias: 'item', max_items: 10,
    })) return 'for-each-item';
  return null;
}

function hasSupportedGuidedBindings(value: unknown, nestedInLoop: boolean): boolean {
  if (typeof value === 'string') {
    if (!isConnectGuidedRuntimeLikeString(value)) return true;
    return isConnectGuidedBindingToken(value)
      && (nestedInLoop || !isConnectGuidedCurrentItemBindingToken(value));
  }
  if (Array.isArray(value)) return value.every(item => hasSupportedGuidedBindings(item, nestedInLoop));
  return !isRecord(value) || Object.values(value).every(item => hasSupportedGuidedBindings(item, nestedInLoop));
}

function isOutcomeNode(node: ConnectWorkflowNode, route: 'urgent' | 'standard'): boolean {
  return node.type === 'control.transform' && node.ref === 'system.transform'
    && isRecord(node['input']) && node['input']['route'] === route;
}

function mapNodeId(result: Map<string, string>, node: ConnectWorkflowNode, canonicalId: string): void {
  if (result.has(node.id)) throw new Error('Workflow node identity is ambiguous.');
  result.set(node.id, canonicalId);
}

function exactlyOne<T>(values: T[]): T {
  if (values.length !== 1) throw new Error('Workflow graph authority is ambiguous.');
  return values[0];
}

function canonicalGuidedTags(tags: unknown, nodes: ConnectWorkflowNode[]): string[] {
  const sourceTags = Array.isArray(tags)
    ? tags.filter((tag): tag is string => typeof tag === 'string')
    : [];
  const usesConnection = nodes.some(node => node.type === 'api.connector');
  const expected = usesConnection
    ? ['guided', 'local-demo', 'connected-account']
    : ['guided', 'provider-free', 'local-demo'];
  const actualManaged = sourceTags.filter(tag => GUIDED_TAGS.has(tag));
  if (actualManaged.length !== expected.length
    || expected.some(tag => !actualManaged.includes(tag))) {
    throw new Error('Workflow guided tags are invalid.');
  }
  return expected;
}

function requiredMappedId(map: Map<string, string>, id: string): string {
  const mapped = map.get(id);
  if (!mapped) throw new Error('Workflow graph contains an unresolved node identity.');
  return mapped;
}

function edgeIdentity(edge: ConnectWorkflowEdge): string {
  return JSON.stringify([edge.source, edge.target, edge.source_handle ?? null]);
}

function allocateUniqueId(suggestedId: string, usedIds: Set<string>): string {
  if (!usedIds.has(suggestedId)) {
    usedIds.add(suggestedId);
    return suggestedId;
  }
  let suffix = 2;
  while (usedIds.has(`${suggestedId}-${suffix}`)) suffix += 1;
  const allocated = `${suggestedId}-${suffix}`;
  usedIds.add(allocated);
  return allocated;
}

function moduleNodeId(operationRef: string): string {
  return `module-${operationRef.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
}

function blockSignature(block: ConnectGuidedBlock): string {
  if (block.kind !== 'connector-operation') return block.kind;
  return JSON.stringify([
    block.kind,
    block.operationRef,
    block.connectionSlot,
    block.connectionProviderRef,
  ]);
}

function visitBlocks(blocks: ConnectGuidedBlock[], visit: (block: ConnectGuidedBlock) => void): void {
  blocks.forEach(block => {
    visit(block);
    if (block.kind === 'for-each-item') visitBlocks(block.body, visit);
    if (block.kind === 'priority-decision') {
      visitBlocks(block.urgent.blocks, visit);
      visitBlocks(block.standard.blocks, visit);
    }
  });
}

function readNestedString(node: ConnectWorkflowNode, objectKey: string, valueKey: string): string {
  const nested = node[objectKey];
  const value = isRecord(nested) ? nested[valueKey] : null;
  if (typeof value !== 'string') throw new Error('Compiled workflow value is invalid.');
  return value;
}

function cloneJson<T>(value: T): T {
  if (value === undefined) return value;
  return JSON.parse(JSON.stringify(value)) as T;
}

function jsonEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
